// Reusable Modal backed by MUI Dialog. One implementation for the whole app.
//
// Props:
//   open      bool
//   onClose   () => void
//   title     string
//   subtitle  string (optional)
//   size      'sm' | 'md' | 'lg' (default 'md')
//   footer    React node (optional, rendered right-aligned)
//   children  React node (the body)

import React from 'react';
import { Dialog, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { THEME } from '../../pages/enterprise-ai/genesisData';

const WIDTHS = { sm: 420, md: 680, lg: 920 };

export const Modal = ({ open, onClose, title, subtitle, size = 'md', footer, children }) => {
  const maxWidth = WIDTHS[size] || WIDTHS.md;
  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          background: THEME.card,
          color: THEME.textPrimary,
          border: `1px solid ${THEME.border}`,
          borderRadius: '12px',
          width: '100%',
          maxWidth,
          m: 2,
          fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          py: 2,
          borderBottom: `1px solid ${THEME.border}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title && (
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: THEME.textPrimary, lineHeight: 1.3 }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: THEME.textMuted, '&:hover': { color: THEME.textPrimary } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>{children}</Box>
      {footer && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${THEME.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {footer}
        </Box>
      )}
    </Dialog>
  );
};

export default Modal;
